import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ShieldCheck, CreditCard, ClockClockwise } from "@phosphor-icons/react";
import Footer from "@/components/Footer";
import RecentWorkGallery from "@/components/RecentWorkGallery";

// What we handle — split into core recurring/deep services and à-la-carte add-ons.
// (Legacy multi-vertical service list preserved in server.py LEGACY_TASK_CATEGORIES.)
const STANDARD_SERVICES = [
    { label: "Weekly · Bi-weekly · Monthly", note: "Recurring care, automatically scheduled" },
    { label: "Move-in / Move-out Deep Clean", note: "Spotless before they walk in" },
    { label: "One-time Deep Clean", note: "Top-to-bottom reset, any time" },
];
const ADDON_SERVICES = [
    { label: "Exterior Windows", note: "Sun-catching, streak-free" },
    { label: "Oven & Refrigerator Interior", note: "The places others skip" },
    { label: "Garage Cleaning", note: "From floor to rafters" },
    { label: "Laundry & Folding", note: "Hotel-style turndown, on request" },
];

const heroBg = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400&fit=max";
const aboutImg = "https://images.unsplash.com/photo-1765767056681-9583b29007cf?crop=entropy&cs=srgb&fm=jpg&q=90&w=1400&fit=max";
const kitchenImg = "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?crop=entropy&cs=srgb&fm=jpg&q=90&w=900&fit=max";
const linensImg = "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?crop=entropy&cs=srgb&fm=jpg&q=90&w=900&fit=max";

export default function LandingPage() {
    return (
        <div data-testid="landing-page" className="relative">
            <div className="grain" />

            {/* HERO */}
            <section className="relative min-h-[92vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroBg})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/60 to-slate-950" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-24 sm:pt-36 pb-20 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="label-eyebrow mb-6"
                        >
                            <span className="text-gold">/</span> Premium home concierge
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif leading-[1.05] tracking-tight"
                        >
                            A butler for every<br />
                            <span className="italic gold-text">corner</span> of your home.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed"
                        >
                            Premium residential cleaning, on your schedule. Tell us what your home
                            needs, get a transparent AI quote reviewed by a human Butler, and pay
                            securely — your space sparkles, you don&apos;t lift a finger.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="mt-10 flex flex-wrap gap-3"
                        >
                            <Link to="/pricing" data-testid="hero-cta-pricing" className="btn-gold">
                                See your price instantly
                            </Link>
                            <Link to="/login" data-testid="hero-cta-login" className="btn-ghost">
                                I already have an account
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 0.6 }}
                            className="mt-14 grid grid-cols-3 max-w-md gap-8"
                        >
                            <div>
                                <div className="font-serif text-3xl gold-text">2 min</div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">AI quote</div>
                            </div>
                            <div>
                                <div className="font-serif text-3xl gold-text">100%</div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Vetted pros</div>
                            </div>
                            <div>
                                <div className="font-serif text-3xl gold-text">24/7</div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Concierge</div>
                            </div>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-4 hidden lg:block">
                        <div className="space-y-4 mt-10">
                            <div className="bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 rounded-sm">
                                <div className="label-eyebrow">Today&apos;s request</div>
                                <div className="mt-3 font-serif text-2xl">&ldquo;Bi-weekly home cleaning&rdquo;</div>
                                <div className="mt-4 flex justify-between items-end">
                                    <div className="text-xs text-slate-400">Estimated</div>
                                    <div className="font-serif text-3xl gold-text">$185</div>
                                </div>
                            </div>
                            <div className="bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 rounded-sm">
                                <div className="label-eyebrow">Butler reviewed</div>
                                <div className="mt-3 text-slate-300 text-sm leading-relaxed">
                                    Quote confirmed. Your Butler team is scheduled for Thursday at 10 AM.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT WE HANDLE — replaced the multi-vertical marquee (Feb 2026 pivot to cleaning-only).
                 Legacy marquee preserved in git history; restore by reverting to commit before pivot. */}
            <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-950 to-slate-900 border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6 sm:px-10">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <div className="label-eyebrow mb-3">/ What we handle</div>
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight">
                                Every <span className="italic gold-text">surface</span>.<br />
                                Every <span className="italic gold-text">corner</span>.
                            </h2>
                            <p className="mt-4 text-slate-400 text-sm leading-relaxed max-w-sm">
                                Recurring or one-time. The places most services skip. Booked in two minutes, handled by a vetted Butler.
                            </p>
                        </div>
                        <div className="lg:col-span-8 space-y-8">
                            <div>
                                <div className="label-eyebrow text-gold mb-3">/ Standard services</div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {STANDARD_SERVICES.map((s) => (
                                        <div
                                            key={s.label}
                                            data-testid={`svc-tile-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="bg-slate-900/60 border border-white/10 p-5 rounded-sm hover:border-gold/40 transition group"
                                        >
                                            <div className="font-serif text-lg text-white group-hover:text-gold transition">{s.label}</div>
                                            <div className="text-xs text-slate-400 mt-1">{s.note}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="label-eyebrow text-gold">/ Add-ons</div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500">à la carte</div>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {ADDON_SERVICES.map((s) => (
                                        <div
                                            key={s.label}
                                            data-testid={`addon-tile-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="bg-slate-900/30 border border-dashed border-white/10 p-5 rounded-sm hover:border-gold/40 transition group"
                                        >
                                            <div className="font-serif text-base text-white group-hover:text-gold transition">{s.label}</div>
                                            <div className="text-xs text-slate-400 mt-1">{s.note}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-20 sm:py-28 max-w-7xl mx-auto px-6 sm:px-10">
                <div className="grid lg:grid-cols-12 gap-10 sm:gap-12">
                    <div className="lg:col-span-4">
                        <div className="label-eyebrow mb-4">/ How it works</div>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight">
                            Four steps to a <span className="italic gold-text">spotless</span> home.
                        </h2>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                            { n: "01", t: "Describe", d: "Tell us what's needed — photos welcome.", I: Sparkle },
                            { n: "02", t: "AI quote", d: "We estimate the cost instantly using AI.", I: ClockClockwise },
                            { n: "03", t: "Butler review", d: "A human Butler verifies & schedules.", I: ShieldCheck },
                            { n: "04", t: "Pay & relax", d: "Approve, pay securely, watch it get done.", I: CreditCard },
                        ].map((step) => (
                            <div key={step.n} className="bg-slate-900/40 border border-white/5 p-8 rounded-sm hover:-translate-y-1 transition-transform">
                                <div className="flex justify-between items-start">
                                    <div className="font-serif text-2xl gold-text">{step.n}</div>
                                    <step.I size={28} weight="duotone" className="text-gold/70" />
                                </div>
                                <div className="mt-8 font-serif text-2xl">{step.t}</div>
                                <p className="mt-2 text-slate-400 text-sm leading-relaxed">{step.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section className="py-16 sm:py-24 max-w-7xl mx-auto px-6 sm:px-10">
                <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-center">
                    <div className="lg:col-span-6 relative">
                        <img src={aboutImg} alt="Luxury interior" className="w-full h-72 sm:h-[420px] lg:h-[520px] object-cover rounded-sm" />
                        <div className="absolute -bottom-6 -right-6 bg-slate-900 border border-white/10 p-6 rounded-sm w-64 hidden sm:block">
                            <div className="label-eyebrow">Trusted by</div>
                            <div className="font-serif text-3xl gold-text mt-2">1,200+ homes</div>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="label-eyebrow mb-4">/ About</div>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight">
                            Not your average<br />
                            cleaning service.<br />
                            <span className="italic gold-text">A standard</span> of its own.
                        </h2>
                        <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed">
                            Every Butler is hand-selected, insured, and trained on a checklist
                            developed for discerning homes. Same Butler every visit when you book
                            recurring — so they learn your space the way a houseguest learns yours.
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-6">
                            <img src={kitchenImg} alt="Pristine modern kitchen after a Butler visit" className="w-full h-44 sm:h-72 object-cover rounded-sm" />
                            <img src={linensImg} alt="Hotel-style fresh linens" className="w-full h-44 sm:h-72 object-cover rounded-sm" />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 sm:py-28 border-t border-white/5">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <div className="label-eyebrow mb-4 flex justify-center">/ Ready when you are</div>
                    <h2 className="text-3xl sm:text-5xl md:text-6xl font-serif leading-tight">
                        Your home, <span className="italic gold-text">handled</span>.
                    </h2>
                    <p className="mt-6 text-slate-400">First task quote in under 2 minutes — no commitments.</p>
                    <div className="mt-10 flex justify-center gap-3">
                        <Link to="/pricing" data-testid="cta-start" className="btn-gold">Get my instant price</Link>
                    </div>
                </div>
            </section>

            {/* RECENT WORK */}
            <section className="max-w-7xl mx-auto px-6 sm:px-10">
                <RecentWorkGallery limit={8} />
                <div className="text-center mt-6">
                    <Link to="/recent-work" data-testid="see-all-recent-work" className="text-xs uppercase tracking-widest text-gold hover:underline">
                        See all recent work →
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
